/** SUNTRONIX ONE — Stock & Inventory dialogs (Stock In / Out / GRN / Warehouse). */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScanButton } from "@/components/ScanButton";

export const Field = ({ label, children, required }) => (
  <label className="text-[10px] text-slate-500 block">
    {label}{required ? " *" : ""}
    {children}
  </label>
);

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";

export const ProductPicker = ({ value, onPick, testid }) => {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q) { setOpts([]); return; }
    const t = setTimeout(() =>
      api.get("/inventory/products", { params: { q } })
        .then(({ data }) => setOpts(data.items || [])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <input value={value ? `${value.product_name}` : q}
          onChange={(e) => { setQ(e.target.value); onPick(null); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search product / SKU / part no…"
          data-testid={testid}
          className={inputCls} />
      {open && opts.length > 0 && !value && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
          {opts.map((p) => (
            <button key={p.id} type="button"
                onClick={() => { onPick(p); setOpen(false); }}
                data-testid={`${testid}-opt-${p.id}`}
                className="w-full text-left px-2 py-1.5 hover:bg-slate-50 text-[11.5px]">
              <div className="font-medium">{p.product_name}</div>
              <div className="text-[10px] text-slate-500">
                {p.brand} · {p.sku || p.product_code || "—"} · {p.tracking_method}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Modal = ({ title, onClose, children, wide }) => {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
  <div className="fixed inset-0 z-50 grid place-items-center p-4">
    <div className="absolute inset-0 bg-black/30" onClick={onClose} />
    <div className={"relative bg-white rounded-xl shadow-xl w-full overflow-y-auto max-h-[92vh] " +
        (wide ? "max-w-3xl" : "max-w-xl")}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
        <div className="text-[13px] font-heading font-semibold">{title}</div>
        <button onClick={onClose} data-testid="modal-close"
            className="text-slate-400 hover:text-slate-700">✕</button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
  );
};

const serialsFromText = (t) => t.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

// ---------------------------------------------------------------------------
export const StockInDialog = ({ meta, onClose, onDone }) => {
  const cfg = meta?.config || {};
  const [f, setF] = useState({ reason: "VENDOR_PURCHASE_RECEIPT", quantity: 1,
    ownership: "SUNTRONIX_OWNED", custody: "SUNTRONIX_WAREHOUSE",
    condition: "NEW", warehouse_id: "", serialText: "", notes: "",
    supplier: "", customer_name: "", order_ref: "" });
  const [product, setProduct] = useState(null);
  const [busy, setBusy] = useState(false);
  const serialTracked = (product?.tracking_method || "SERIAL") === "SERIAL";

  const submit = async () => {
    if (!product) { toast.error("Pick a product"); return; }
    if (!f.warehouse_id) { toast.error("Pick a warehouse"); return; }
    const serials = serialsFromText(f.serialText);
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/stock-in", {
        reason: f.reason, product_id: product.id,
        quantity: Number(f.quantity) || 1, serials,
        warehouse_id: f.warehouse_id, ownership: f.ownership,
        custody: f.custody, condition: f.condition,
        supplier: f.supplier || null, customer_name: f.customer_name || null,
        order_ref: f.order_ref || null, notes: f.notes || null,
      });
      toast.success(`Stock in recorded — ${data.movement.code}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Stock in failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="+ Stock In" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="stock-in-dialog">
        <div className="col-span-2">
          <Field label="Product" required>
            <ProductPicker value={product} onPick={setProduct} testid="stock-in-product" />
          </Field>
          {product && (
            <div className="text-[10px] text-slate-500 mt-1">
              Tracking: <b>{product.tracking_method}</b>
              {serialTracked ? " — one serial per unit required" : " — quantity only"}
            </div>
          )}
        </div>
        <Field label="Reason" required>
          <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}
              data-testid="stock-in-reason" className={inputCls}>
            {(cfg.inward_reasons || []).map((r) =>
              <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Warehouse" required>
          <select value={f.warehouse_id}
              onChange={(e) => setF({ ...f, warehouse_id: e.target.value })}
              data-testid="stock-in-warehouse" className={inputCls}>
            <option value="">— pick —</option>
            {(meta?.warehouses || []).map((w) =>
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
          </select>
        </Field>
        <Field label="Quantity" required>
          <input type="number" min={1} value={f.quantity}
              onChange={(e) => setF({ ...f, quantity: e.target.value })}
              data-testid="stock-in-qty" className={inputCls} />
        </Field>
        <Field label="Condition">
          <select value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })}
              data-testid="stock-in-condition" className={inputCls}>
            {(cfg.conditions || []).map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Ownership">
          <select value={f.ownership} onChange={(e) => setF({ ...f, ownership: e.target.value })}
              data-testid="stock-in-ownership" className={inputCls}>
            {(cfg.ownership_types || []).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Custody">
          <select value={f.custody} onChange={(e) => setF({ ...f, custody: e.target.value })}
              className={inputCls}>
            {(cfg.custody_types || []).map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        {f.ownership === "CUSTOMER_OWNED" && (
          <Field label="Customer" required>
            <input value={f.customer_name}
                onChange={(e) => setF({ ...f, customer_name: e.target.value })}
                data-testid="stock-in-customer" className={inputCls} />
          </Field>
        )}
        <Field label="Supplier / Source">
          <input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })}
              className={inputCls} />
        </Field>
        <Field label="Related Order Ref">
          <input value={f.order_ref} onChange={(e) => setF({ ...f, order_ref: e.target.value })}
              className={inputCls} />
        </Field>
        {serialTracked && (
          <div className="col-span-2">
            <Field label={`Serial numbers (${serialsFromText(f.serialText).length}/${f.quantity}) — scan, type or paste`}>
              <div className="flex items-start gap-1">
                <textarea rows={3} value={f.serialText}
                    onChange={(e) => setF({ ...f, serialText: e.target.value })}
                    data-testid="stock-in-serials"
                    className={inputCls + " font-mono"} />
                <div className="mt-1"><ScanButton testid="stock-in-scan"
                    onScan={(v) => setF((x) => ({ ...x, serialText: x.serialText ? x.serialText + "\n" + v : v }))} /></div>
              </div>
            </Field>
          </div>
        )}
        <div className="col-span-2">
          <Field label="Notes">
            <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                className={inputCls} />
          </Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="stock-in-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-800 text-white disabled:opacity-40">
          {busy ? "Recording…" : "Record Stock In"}
        </button>
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
export const StockOutDialog = ({ meta, onClose, onDone }) => {
  const cfg = meta?.config || {};
  const [f, setF] = useState({ reason: "CUSTOMER_SALE", quantity: 1,
    warehouse_id: "", serialText: "", destination: "", order_ref: "", notes: "" });
  const [product, setProduct] = useState(null);
  const [availUnits, setAvailUnits] = useState([]);
  const [busy, setBusy] = useState(false);
  const serialTracked = (product?.tracking_method || "SERIAL") === "SERIAL";

  useEffect(() => {
    if (!product || !f.warehouse_id || !serialTracked) { setAvailUnits([]); return; }
    api.get("/inventory/units", { params: { warehouse_id: f.warehouse_id, status: "AVAILABLE", product_id: product.id, limit: 100 } })
      .then(({ data }) => setAvailUnits(data.items || []))
      .catch(() => toast.error("Could not load available serials"));
  }, [product, f.warehouse_id, serialTracked]);

  const pickSerial = (s) => {
    const cur = serialsFromText(f.serialText);
    if (cur.includes(s)) return;
    setF({ ...f, serialText: [...cur, s].join("\n") });
  };

  const submit = async () => {
    if (!product || !f.warehouse_id) { toast.error("Pick product and warehouse"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/stock-out", {
        reason: f.reason, product_id: product.id,
        quantity: Number(f.quantity) || 1,
        serials: serialsFromText(f.serialText),
        warehouse_id: f.warehouse_id,
        destination: f.destination || null,
        order_ref: f.order_ref || null, notes: f.notes || null,
        issued_to: f.issued_to || null,
        expected_return: f.expected_return || null,
      });
      toast.success(`Stock out recorded — ${data.movement.code} (${data.end_status})`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Stock out failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Stock Out" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="stock-out-dialog">
        <div className="col-span-2">
          <Field label="Product" required>
            <ProductPicker value={product} onPick={setProduct} testid="stock-out-product" />
          </Field>
        </div>
        <Field label="Reason" required>
          <select value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}
              data-testid="stock-out-reason" className={inputCls}>
            {(cfg.outward_reasons || []).map((r) =>
              <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="From Warehouse" required>
          <select value={f.warehouse_id}
              onChange={(e) => setF({ ...f, warehouse_id: e.target.value })}
              data-testid="stock-out-warehouse" className={inputCls}>
            <option value="">— pick —</option>
            {(meta?.warehouses || []).map((w) =>
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
          </select>
        </Field>
        <Field label="Quantity" required>
          <input type="number" min={1} value={f.quantity}
              onChange={(e) => setF({ ...f, quantity: e.target.value })}
              data-testid="stock-out-qty" className={inputCls} />
        </Field>
        <Field label="Destination / Customer">
          <input value={f.destination}
              onChange={(e) => setF({ ...f, destination: e.target.value })}
              data-testid="stock-out-destination" className={inputCls} />
        </Field>
        {(f.reason === "DEMO" || f.reason === "LOANER") && (
          <>
            <Field label="Issued to (person)">
              <input value={f.issued_to || ""} onChange={(e) => setF({ ...f, issued_to: e.target.value })}
                  data-testid="stock-out-issued-to" className={inputCls} />
            </Field>
            <Field label="Expected return date">
              <input type="date" value={f.expected_return || ""}
                  onChange={(e) => setF({ ...f, expected_return: e.target.value })}
                  data-testid="stock-out-expected-return" className={inputCls} />
            </Field>
          </>
        )}
        <Field label="Order / Shipment Ref">
          <input value={f.order_ref} onChange={(e) => setF({ ...f, order_ref: e.target.value })}
              className={inputCls} />
        </Field>
        {serialTracked && (
          <div className="col-span-2 space-y-1">
            <Field label={`Serials to issue (${serialsFromText(f.serialText).length}/${f.quantity})`}>
              <div className="flex items-start gap-1">
                <textarea rows={2} value={f.serialText}
                    onChange={(e) => setF({ ...f, serialText: e.target.value })}
                    data-testid="stock-out-serials"
                    className={inputCls + " font-mono"} />
                <div className="mt-1"><ScanButton testid="stock-out-scan"
                    onScan={(v) => setF((x) => ({ ...x, serialText: x.serialText ? x.serialText + "\n" + v : v }))} /></div>
              </div>
            </Field>
            {availUnits.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {availUnits.slice(0, 20).map((u) => (
                  <button key={u.id} type="button" onClick={() => pickSerial(u.serial)}
                      data-testid={`stock-out-pick-${u.serial}`}
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-300 hover:bg-emerald-50">
                    {u.serial}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="col-span-2">
          <Field label="Notes">
            <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                className={inputCls} />
          </Field>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="stock-out-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-rose-800 text-white disabled:opacity-40">
          {busy ? "Recording…" : "Record Stock Out"}
        </button>
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
export const ReceiveGoodsDialog = ({ meta, onClose, onDone }) => {
  const [pos, setPos] = useState([]);
  const [po, setPo] = useState(null);
  const [warehouseId, setWarehouseId] = useState("");
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/inventory/vendor-pos").then(({ data }) => setPos(data.items || []));
  }, []);

  const selectPo = (p) => {
    setPo(p);
    setLines((p.items || []).map((it) => ({
      line_index: it.line_index, product_name: it.product_name,
      product_code: it.product_code,
      expected_qty: it.expected_qty, prior: it.received_qty,
      outstanding: it.outstanding_qty,
      received_qty: it.outstanding_qty, serialText: "",
      condition: "NEW", product: null,
    })));
  };

  const patchLine = (i, upd) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...upd } : l)));

  const submit = async () => {
    if (!po || !warehouseId) { toast.error("Pick a Vendor PO and warehouse"); return; }
    const payloadLines = [];
    for (const l of lines) {
      const qty = Number(l.received_qty) || 0;
      if (qty <= 0) continue;
      if (!l.product) { toast.error(`Map a product for "${l.product_name}"`); return; }
      payloadLines.push({
        line_index: l.line_index, product_id: l.product.id,
        received_qty: qty, serials: serialsFromText(l.serialText),
        condition: l.condition,
      });
    }
    if (!payloadLines.length) { toast.error("Enter at least one received quantity"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/grn", {
        vendor_po_id: po.id, warehouse_id: warehouseId, lines: payloadLines,
      });
      if (data.grn.status === "REVIEW_REQUIRED") {
        toast.warning(`${data.grn.code} — OVER RECEIPT, management review required before stock is created`);
      } else {
        toast.success(`${data.grn.code} — ${data.grn.receipt_type.replace(/_/g, " ")} · ${data.units_created} unit(s) in stock`);
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Receipt failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Receive Goods — Vendor PO → GRN" onClose={onClose} wide>
      <div className="space-y-3" data-testid="receive-goods-dialog">
        {!po ? (
          <div className="space-y-1">
            <div className="text-[11px] text-slate-500 mb-1">Open Vendor POs</div>
            {pos.filter((p) => !p.fully_received).map((p) => (
              <button key={p.id} onClick={() => selectPo(p)}
                  data-testid={`grn-po-${p.code}`}
                  className="w-full text-left border border-slate-200 rounded-md p-2.5 hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold font-mono">{p.code}</span>
                  <span className="text-[11px] text-slate-600">{p.vendor_name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100">{p.status}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {(p.items || []).map((i) =>
                    `${i.product_name} (${i.received_qty}/${i.expected_qty})`).join(" · ")}
                </div>
              </button>
            ))}
            {pos.filter((p) => !p.fully_received).length === 0 && (
              <div className="text-[11px] text-slate-400 py-4 text-center">
                No open Vendor POs with outstanding quantity.
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-mono font-semibold">{po.code}</span>
              <span className="text-[11px] text-slate-600">{po.vendor_name}</span>
              <button onClick={() => setPo(null)}
                  className="text-[10px] text-slate-500 underline">change PO</button>
              <div className="flex-1" />
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
                  data-testid="grn-warehouse" className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5">
                <option value="">Receive into warehouse…</option>
                {(meta?.warehouses || []).map((w) =>
                  <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
              </select>
            </div>
            {lines.map((l, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-2.5 space-y-2"
                  data-testid={`grn-line-${i}`}>
                <div className="flex items-center gap-2 text-[11.5px]">
                  <span className="font-semibold">{l.product_name}</span>
                  <span className="text-[10px] text-slate-500">
                    expected {l.expected_qty} · already received {l.prior} · outstanding {l.outstanding}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Map to Product Master" required>
                    <ProductPicker value={l.product}
                        onPick={(p) => patchLine(i, { product: p })}
                        testid={`grn-line-${i}-product`} />
                  </Field>
                  <Field label="Received qty" required>
                    <input type="number" min={0} value={l.received_qty}
                        onChange={(e) => patchLine(i, { received_qty: e.target.value })}
                        data-testid={`grn-line-${i}-qty`} className={inputCls} />
                  </Field>
                  <Field label="Condition">
                    <select value={l.condition}
                        onChange={(e) => patchLine(i, { condition: e.target.value })}
                        data-testid={`grn-line-${i}-condition`} className={inputCls}>
                      {(meta?.config?.conditions || []).map((c) =>
                        <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                    </select>
                  </Field>
                </div>
                {(l.product?.tracking_method || "SERIAL") === "SERIAL" && (
                  <Field label={`Serials (${serialsFromText(l.serialText).length}/${l.received_qty}) — scan or paste, one per line`}>
                    <div className="flex items-start gap-1">
                      <textarea rows={2} value={l.serialText}
                          onChange={(e) => patchLine(i, { serialText: e.target.value })}
                          data-testid={`grn-line-${i}-serials`}
                          className={inputCls + " font-mono"} />
                      <div className="mt-1"><ScanButton testid={`grn-line-${i}-scan`}
                          onScan={(v) => patchLine(i, { serialText: l.serialText ? l.serialText + "\n" + v : v })} /></div>
                    </div>
                  </Field>
                )}
              </div>
            ))}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="grn-submit"
                  className="text-[11px] px-3 py-1.5 rounded-md bg-emerald-800 text-white disabled:opacity-40">
                {busy ? "Creating GRN…" : "Create Goods Receipt"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
export const WarehouseDialog = ({ warehouse, onClose, onDone }) => {
  const [f, setF] = useState(warehouse || {
    code: "", name: "", legal_entity: "", country: "IN", address: "", contact: "",
    status: "ACTIVE",
  });
  const [busy, setBusy] = useState(false);
  const isNew = !warehouse?.id;

  const save = async () => {
    if (!f.code || !f.name) { toast.error("Code and name required"); return; }
    setBusy(true);
    try {
      if (isNew) await api.post("/inventory/warehouses", f);
      else await api.patch(`/inventory/warehouses/${f.id}`, {
        name: f.name, legal_entity: f.legal_entity, country: f.country,
        address: f.address, contact: f.contact, status: f.status,
      });
      toast.success(isNew ? "Warehouse created" : "Warehouse updated");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isNew ? "New Warehouse" : `Edit ${f.code}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3" data-testid="warehouse-dialog">
        <Field label="Warehouse Code" required>
          <input value={f.code} disabled={!isNew}
              onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
              data-testid="wh-code" className={inputCls + " font-mono"} />
        </Field>
        <Field label="Name" required>
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              data-testid="wh-name" className={inputCls} />
        </Field>
        <Field label="Legal Entity">
          <input value={f.legal_entity || ""} onChange={(e) => setF({ ...f, legal_entity: e.target.value })}
              className={inputCls} />
        </Field>
        <Field label="Country">
          <input value={f.country || ""} onChange={(e) => setF({ ...f, country: e.target.value })}
              className={inputCls} />
        </Field>
        <div className="col-span-2">
          <Field label="Address">
            <input value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })}
                className={inputCls} />
          </Field>
        </div>
        <Field label="Contact">
          <input value={f.contact || ""} onChange={(e) => setF({ ...f, contact: e.target.value })}
              className={inputCls} />
        </Field>
        <Field label="Status">
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
              className={inputCls}>
            <option>ACTIVE</option><option>INACTIVE</option>
          </select>
        </Field>
      </div>
      {!isNew && <LocationsEditor warehouseId={f.id} />}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="wh-save"
            className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
          {busy ? "Saving…" : isNew ? "Create warehouse" : "Save changes"}
        </button>
      </div>
    </Modal>
  );
};

function LocationsEditor({ warehouseId }) {
  const [locs, setLocs] = useState([]);
  const [n, setN] = useState({ zone: "", rack: "", shelf: "", bin: "" });
  const load = useCallback(() => api.get(`/inventory/warehouses/${warehouseId}/locations`)
    .then(({ data }) => setLocs(data.items || [])), [warehouseId]);
  useEffect(() => { load(); }, [load, warehouseId]);

  const add = async () => {
    try {
      await api.post(`/inventory/warehouses/${warehouseId}/locations`, n);
      setN({ zone: "", rack: "", shelf: "", bin: "" });
      toast.success("Location added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (l) => {
    try {
      await api.delete(`/inventory/warehouses/${warehouseId}/locations/${l.id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-3" data-testid="locations-editor">
      <div className="text-[11px] font-semibold text-slate-700 mb-2">
        Locations (Zone → Rack → Shelf → Bin)
      </div>
      <div className="flex items-end gap-1.5 mb-2">
        {["zone", "rack", "shelf", "bin"].map((k) => (
          <label key={k} className="text-[9px] uppercase text-slate-500">
            {k}
            <input value={n[k]} onChange={(e) => setN({ ...n, [k]: e.target.value.toUpperCase() })}
                data-testid={`loc-${k}`}
                className="mt-0.5 w-16 text-[11px] border border-slate-200 rounded px-1.5 py-1 font-mono" />
          </label>
        ))}
        <button onClick={add} data-testid="loc-add"
            className="text-[10.5px] px-2.5 py-1.5 rounded bg-slate-900 text-white">+ Add</button>
      </div>
      <div className="flex flex-wrap gap-1">
        {locs.map((l) => (
          <span key={l.id} className="text-[10px] font-mono px-1.5 py-1 rounded bg-slate-100 inline-flex items-center gap-1"
              data-testid={`loc-chip-${l.id}`}>
            {l.label}
            <button onClick={() => del(l)} className="text-rose-600 hover:text-rose-800">✕</button>
          </span>
        ))}
        {!locs.length && <span className="text-[10px] text-slate-400">No locations yet — stock can still be received at warehouse level.</span>}
      </div>
    </div>
  );
}
